import { lazy, Suspense } from 'react';
import { ReviewedBy } from "@/components/ReviewedBy";
import { CalculatorSkeleton } from "@/components/calculators/CalculatorSkeleton";
import { DeferredSection } from "@/components/DeferredSection";
const MortgageCalculator = lazy(() => import("@/components/calculators/MortgageCalculator").then(m => ({ default: m.MortgageCalculator })));
const InterestRateChecker = lazy(() => import("@/components/calculators/InterestRateChecker").then(m => ({ default: m.InterestRateChecker })));
const KALPCalculator = lazy(() => import("@/components/calculators/KALPCalculator").then(m => ({ default: m.KALPCalculator })));
const Footer = lazy(() => import("@/components/Footer"));
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StickyCTA } from "@/components/StickyCTA";
import { useStickyCTA } from "@/hooks/useStickyCTA";
import { Home, Percent, Shield, CheckCircle2, Info, Calculator, FileText, AlertCircle, ArrowRight, ChevronDown, Layout, Eye, Users, ExternalLink } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { SEO } from "@/components/SEO";
import { FeaturedProductCard } from "@/components/FeaturedProductCard";
import { topMortgages } from "@/data/featuredProducts";
import { MortgageRatesTable } from "@/components/MortgageRatesTable";
import { MortgageProductCard } from "@/components/MortgageProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ComparisonMethodology } from "@/components/content/ComparisonMethodology";
import { DecisionPathLinks } from "@/components/content/DecisionPathLinks";
import { generateComparisonPageSchema } from "@/utils/schemaGenerators";
import { MORTGAGE_BANKS } from "@/utils/mortgageBanks";

const BANKS = MORTGAGE_BANKS;

const mortgageSchemas = generateComparisonPageSchema({
  name: "Jämför bolån och bolåneräntor",
  description: "Klarfinans jämför bolån, bolåneförmedlare, listräntor och snitträntor från svenska banker.",
  url: "/sv-SE/bolan",
  category: "Bolån",
  breadcrumbs: [
    { name: "Start", url: "https://klarfinans.se" },
    { name: "Bolån" },
  ],
  items: [
    {
      name: "Ordna Bolån",
      provider: "Ordna Bolån",
      url: "/sv-SE/bolan/ordna-bolan",
      description: "Bolåneförmedlare som hjälper användare jämföra flera bolånealternativ.",
      amount: "Upp till 20 000 000 kr",
      interestRate: "Individuell bolåneränta",
      term: "Rörlig och bunden ränta",
      creditCheck: "UC och bankens kreditprövning",
    },
    ...BANKS.map((bank) => ({
      name: `${bank.name} bolån`,
      provider: bank.name,
      url: `/sv-SE/banker/${bank.slug}`,
      description: `Bolåneräntor, listräntor och snitträntor för ${bank.name}.`,
      interestRate: "Se aktuell listränta och snittränta",
      term: "Rörlig och bunden ränta",
      creditCheck: "UC och bankens kreditprövning",
    })),
  ],
});

const MortgagePage = () => {
  const navigate = useNavigate();
  const {
    currentLanguage
  } = useLanguage();
  const showStickyCTA = useStickyCTA();

  const scrollToCalculator = () => {
    const calculatorSection = document.getElementById('calculator');
    if (calculatorSection) {
      calculatorSection.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  const scrollToRatesTable = () => {
    const ratesSection = document.getElementById('mortgage-rates');
    if (ratesSection) {
      ratesSection.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  return <div className="min-h-screen flex flex-col">
      <SEO
        title="Bolån - Jämför bolån från flera banker | KlarFinans"
        description="Låna upp till 20 000 000 kr med konkurrenskraftiga räntor. Jämför bolåneerbjudanden från flera banker och hitta bästa bolånet för dina behov."
        keywords={["bolån", "bostadslån", "jämför bolån", "bolåneränta", "lånelöfte", "amortering"]}
        schemaData={mortgageSchemas}
      />
      
      <main className="flex-1">
      {/* Hero Section */}
        <section className="relative overflow-hidden gradient-mortgage-page text-white pt-20 lg:pt-28 pb-8 md:pb-12 lg:pb-32">
          <div className="container mx-auto px-3 md:px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-start">
              {/* Left: Text Content */}
              <div className="space-y-4 md:space-y-6">
                <Badge className="mb-4 bg-white/20 text-white border-white/30" variant="outline">
                  <Home className="w-4 h-4 mr-2" />
                  Bolån
                </Badge>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">
                  Jämför bolån från flera banker
                </h1>
                <p className="text-base md:text-xl opacity-90 mb-6 md:mb-8">
                  Se snitträntor, jämför villkor och förstå vad som påverkar ditt bolån. 
                  All information samlad för att göra det enklare att ta välgrundade beslut.
                </p>
                
                <div className="flex flex-wrap gap-4 md:gap-6 pt-4">
                  {[{
                  icon: Layout,
                  title: 'Snabb överblick',
                  subtitle: 'Allt på ett ställe'
                }, {
                  icon: Eye,
                  title: 'Transparens',
                  subtitle: 'Tydliga villkor'
                }, {
                  icon: Users,
                  title: 'Konsumentfokus',
                  subtitle: 'Objektiv fakta'
                }].map((item, i) => <div key={i} className="flex items-center gap-2 md:gap-3">
                      <div className="bg-white/10 backdrop-blur-lg rounded-lg p-2 md:p-3">
                        <item.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-white">{item.title}</p>
                        <p className="text-sm text-white/80">{item.subtitle}</p>
                      </div>
                    </div>)}
                </div>

                <div className="hero-cta-section pt-6 flex flex-col gap-3 items-start w-full">
                  <Button
                    size="lg"
                    onClick={scrollToRatesTable}
                    className="w-full sm:w-[85%] md:w-[70%] bg-white text-black hover:bg-white/90 shadow-lg hover:shadow-xl transition-all duration-300 group text-sm sm:text-base px-3 sm:px-4"
                  >
                    <Percent className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 group-hover:scale-110 transition-transform flex-shrink-0" />
                    <span className="truncate">Jämför aktuella bolåneräntor</span>
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1 sm:ml-2 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                  </Button>
                  
                  <Button
                    size="lg"
                    onClick={scrollToCalculator}
                    className="w-full sm:w-[85%] md:w-[70%] bg-white text-black hover:bg-white/90 shadow-lg hover:shadow-xl transition-all duration-300 group text-sm sm:text-base px-3 sm:px-4"
                  >
                    <Calculator className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 group-hover:scale-110 transition-transform flex-shrink-0" />
                    <span className="truncate">Gå till bolånekalkylator</span>
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-1 sm:ml-2 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                  </Button>
                </div>
              </div>

              {/* Right: Featured Product Card */}
              <div className="max-w-lg mx-auto lg:mx-0 lg:ml-12 lg:max-w-xl">
                <div className="space-y-3 md:space-y-4">
                  <div className="text-center mb-4">
                    
                    <h2 className="text-xl md:text-2xl font-bold text-white">Bolånejämförelse med nöjdast kunder</h2>
                    <p className="text-white/80 text-sm">Aktuella räntedatum visas i tabellen nedan</p>
                  </div>
                  
                  <FeaturedProductCard 
                    product={topMortgages[0]} 
                    badge="Jämför flera långivare"
                    rateLabel="Snittränta"
                    reviewCount={1292}
                    secondaryButtonText="Läs mer om Ordna Bolån"
                    secondaryButtonLink={`/${currentLanguage}/bolan/ordna-bolan`}
                  />

                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mortgage Rates Section */}
        <section id="mortgage-rates" className="py-8 md:py-12 bg-muted/30">
          <div className="container max-w-5xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-2">Aktuella bolåneräntor</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Jämför listräntor och genomsnittliga räntor från Sveriges största banker.
              </p>
            </div>
            
            <MortgageRatesTable maxYears={5} />
          </div>
        </section>

        <ComparisonMethodology
          title="Så jämför vi bolån"
          description="Bolån behöver jämföras på mer än en listränta. Snitträntor, ränterabatt, bindningstid, amorteringskrav och hushållets marginaler påverkar vad lånet faktiskt kostar."
          items={[
            {
              title: "Listränta är startpunkten",
              body: "Listräntan visar bankens öppna pris, men många kunder får rabatt. Därför behöver användaren även förstå snittränta och förhandlingsläge.",
            },
            {
              title: "Kalkyl före ansökan",
              body: "Bolånekalkylatorn och KALP-guiden hjälper användaren att se om hushållet tål högre ränta, amortering och boendekostnader.",
            },
            {
              title: "Guider stödjer beslutet",
              body: "Interna länkar till lånelöfte, kontantinsats, belåningsgrad och rörlig eller bunden ränta ska ligga nära bolånejämförelsen.",
            },
          ]}
        />


        <DecisionPathLinks
          title="Ta nästa steg i bolåneflödet"
          description="Bolån kräver flera beslut i rätt ordning. Här får användaren snabb väg till räntor, kalkyl och de guider som oftast behövs före bankkontakt."
          links={[
            {
              title: "Jämför bolåneräntor",
              description: "Gå till tabellen med listräntor och snitträntor från svenska banker.",
              href: "#mortgage-rates",
              label: "Till räntorna",
              badge: "Starta här",
            },
            {
              title: "Räkna på bolån",
              description: "Se ungefärlig månadsbetalning och hur räntan påverkar hushållet.",
              href: "#calculator",
              label: "Öppna kalkylatorn",
            },
            {
              title: "Lånelöfte",
              description: "Förstå hur lånelöfte fungerar innan du börjar buda på bostad.",
              href: `/${currentLanguage}/guider/lanelöfte`,
              label: "Läs guiden",
            },
            {
              title: "Kontantinsats",
              description: "Se hur kontantinsats och bolånetak påverkar hur mycket du kan låna.",
              href: `/${currentLanguage}/guider/kontantinsats-och-bolanetak`,
              label: "Läs om kravet",
            },
            {
              title: "Belåningsgrad",
              description: "Räkna ut hur stor del av bostaden som finansieras med lån.",
              href: `/${currentLanguage}/guider/belåningsgrad`,
              label: "Förstå nyckeltalet",
            },
            {
              title: "Rörlig eller bunden ränta",
              description: "Jämför trygghet, flexibilitet och risk innan du väljer bindningstid.",
              href: `/${currentLanguage}/guider/ranta-rorlig-eller-bunden`,
              label: "Läs guiden",
            },
          ]}
        />


        {/* Information Section */}
        <section className="py-8 md:py-12 bg-muted/30">
          <div className="container max-w-4xl">
            <h2 className="text-3xl font-bold mb-8">Så hittar du bästa bolånet</h2>
            
            <div className="prose prose-lg dark:prose-invert max-w-none space-y-6">
              <p>
                När du hittat den bostad du ska köpa – det kan vara en bostadsrätt, 
                fritidshus eller villa – behöver du hitta bästa möjliga bolån för dina 
                personliga förutsättningar. För att få ett så billigt och bra bostadslån 
                som möjligt är det viktigt att du <strong>jämför bankernas räntor och villkor</strong>.
              </p>

              <p>
                Hos KlarFinans kan du enkelt <strong>jämföra bolåneerbjudanden från flera banker</strong>. 
                Vi hjälper dig att hitta det bolån som passar bäst för din ekonomiska situation, 
                med transparenta villkor och konkurrensskraftiga räntor.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Första steget – få ett lånelöfte</h3>
              
              <p>
                När du bestämt dig för att köpa bostad och börjar leta aktivt, är det bra 
                att ha ett <strong>lånelöfte</strong> med dig så att du vet vilken budget 
                du har att röra dig med. Lånelöftet visar det maximala belopp du kan låna, 
                men det är klokt att titta på bostäder något under detta belopp för att:
              </p>

              <ul className="list-disc pl-6 space-y-2">
                <li>Inte vara maximalt belånad</li>
                <li>Ha utrymme för eventuell budgivning</li>
                <li>Kunna hantera oförutsedda utgifter</li>
              </ul>

              <Alert className="my-6 border-primary/50 bg-primary/5">
                <Info className="h-5 w-5" />
                <AlertDescription>
                  <strong>Viktigt:</strong> Ett lånelöfte är ingen garanti på att du faktiskt 
                  får lånet, utan en preliminär bedömning. När du hittat din drömbostad behöver 
                  du ansöka om ett slutgiltigt bolån där banken känner till vilken bostad som 
                  står som säkerhet för lånet.
                </AlertDescription>
              </Alert>

              <h3 className="text-2xl font-bold mt-8 mb-4">Så mycket kan du låna till bostad</h3>
              
              <p>
                Hur mycket du kan låna avgörs av dina <strong>personliga ekonomiska förutsättningar</strong> 
                och det <strong>skuldkvotstak</strong> som banker sätter. Skuldkvotstaket är en övre 
                gräns för hur mycket en låntagare får låna i förhållande till sin inkomst.
              </p>

              <Card className="my-6 bg-muted/50">
                <CardContent className="p-6">
                  <h4 className="font-bold mb-3 flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-primary" />
                    Skuldkvotstak – så räknar du
                  </h4>
                  <p className="mb-3">
                    Varje bank bestämmer skuldkvotstaket själv, men ofta brukar det ligga på 
                    <strong> 5-6 gånger årsinkomsten före skatt</strong>.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Exempel: Med en årsinkomst på 500 000 kr kan du maximalt låna 2 500 000 - 3 000 000 kr, 
                    förutsatt att du klarar av räntekostnaderna och amorteringen.
                  </p>
                </CardContent>
              </Card>

              <h3 className="text-2xl font-bold mt-8 mb-4">Amorteringsregler för bolån</h3>
              
              <p>
                Amortering är det belopp som betalas av på ett lån vid varje betalningstillfälle. 
                Vanligtvis amorterar du en gång i månaden på ditt bolån tillsammans med räntekostnaden. 
                Hur mycket du minst måste amortera styrs av <strong>amorteringskraven</strong>.
              </p>

              <div className="grid md:grid-cols-2 gap-4 my-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Belåningsgrad 50-70%</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary mb-2">1%</p>
                    <p className="text-sm text-muted-foreground">
                      amortering per år av bostadens marknadsvärde
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Belåningsgrad över 70%</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary mb-2">2%</p>
                    <p className="text-sm text-muted-foreground">
                      amortering per år av bostadens marknadsvärde
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Alert className="border-amber-500/50 bg-amber-500">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                <AlertDescription>
                  <strong>Extra amorteringskrav:</strong> Om dina samlade bolån är 4,5 gånger 
                  större än din årsinkomst före skatt behöver du amortera ytterligare 1% per år.
                </AlertDescription>
              </Alert>

            {/* Mortgage Calculator */}
            <div id="calculator" className="my-8">
              <Suspense fallback={<CalculatorSkeleton variant="detailed" />}>
                <MortgageCalculator />
              </Suspense>
            </div>

            <h3 className="text-2xl font-bold mb-4 mt-12">Räkna på kostnaderna för lånet</h3>
              
              <p>
                En bostad är ofta den dyraste investering du gör, därför är det viktigt att 
                räkna på hur mycket ett lån kommer att kosta dig varje månad. Gör en kalkyl 
                och se hur mycket du kan låna utan att det blir för betungande för din ekonomi.
              </p>

              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Ha marginaler för eventuella räntehöjningar</li>
                <li>Räkna med oförutsedda utgifter</li>
                <li>Beakta kontantinsats (minst 15% av bostadens pris)</li>
                <li>Lägg till kostnader för lagfart och pantbrev</li>
              </ul>

            {/* KALP Section */}
            <Card className="my-6 border-primary/20 bg-gradient-to-br from-primary/5 to-background">
              <CardContent className="pt-4 pb-5 px-4 md:px-6">
                <div className="flex items-start gap-3 mb-3">
                  <div className="bg-primary/10 backdrop-blur-lg rounded-lg p-2">
                    <Calculator className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">Förstå din KALP</h3>
                    <p className="text-sm text-muted-foreground">
                      Kvar-Att-Leva-På kalkyl - så bedömer banker din återbetalningsförmåga
                    </p>
                  </div>
                </div>

                <div className="prose dark:prose-invert max-w-none mb-4 text-sm space-y-3">
                    <p>
                      När du ansöker om bolån gör banken en <strong>KALP-kalkyl</strong> (Kvar-Att-Leva-På) 
                      för att säkerställa att du har råd att betala tillbaka lånet. Banken räknar med en 
                      <strong> kalkylränta på cirka 6-8%</strong> – betydligt högre än dagens räntor – 
                      för att testa om du skulle klara av kraftiga räntehöjningar.
                    </p>
                    
                    <p>
                      I beräkningen ingår:
                    </p>
                    
                  <ul className="list-disc pl-5 space-y-1 mb-3 text-sm">
                      <li>Din/er samlade månadsinkomst</li>
                      <li>Levnadskostnader (cirka <strong>9 000 kr/månad per vuxen</strong>)</li>
                      <li>Alla befintliga låneåtaganden</li>
                      <li>Räntekostnader med kalkylränta (6-8%)</li>
                      <li>Amorteringskostnader</li>
                    </ul>

                    <Alert className="my-3 border-primary/50 bg-primary/5 py-2">
                      <Info className="h-5 w-5" />
                      <AlertDescription>
                        <strong>Viktigt:</strong> Din KALP måste vara positiv för att lånet ska beviljas. 
                        Det innebär att du måste ha pengar över varje månad efter alla kostnader.
                      </AlertDescription>
                    </Alert>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => navigate(`/${currentLanguage}/guider/kalp`)}
                      className="gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Läs mer om KALP
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const kalpCalc = document.getElementById('kalp-calculator-section');
                        if (kalpCalc) {
                          const trigger = kalpCalc.querySelector('button[data-state]');
                          if (trigger) {
                            (trigger as HTMLElement).click();
                            setTimeout(() => {
                              kalpCalc.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 100);
                          }
                        }
                      }}
                      className="gap-2 hover:bg-primary hover:text-white hover:border-primary transition-colors"
                    >
                      <Calculator className="h-4 w-4" />
                      Beräkna din KALP
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Collapsible KALP Calculator */}
              <Collapsible id="kalp-calculator-section" className="my-8">
                <CollapsibleTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-between hover:bg-primary hover:text-white hover:border-primary transition-colors group"
                    size="lg"
                  >
                    <span className="flex items-center gap-2">
                      <Calculator className="h-5 w-5" />
                      KALP-kalkylator
                    </span>
                    <ChevronDown className="h-5 w-5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-4">
                  <Card>
                    <CardContent className="pt-6">
                      <Suspense fallback={<CalculatorSkeleton variant="detailed" />}>
                        <KALPCalculator />
                      </Suspense>
                      <div className="mt-4 text-center text-sm text-muted-foreground">
                        <p>* Beräkningarna är vägledande. Banker kan ha olika metoder och krav.</p>
                      </div>
                    </CardContent>
                  </Card>
                </CollapsibleContent>
              </Collapsible>

              <h3 className="text-2xl font-bold mt-8 mb-4">Köp av småhus – extra kostnader</h3>
              
              <p>
                Om du köper villa eller fritidshus behöver du även räkna med kostnaden för 
                <strong> lagfart och pantbrev</strong>, som annars kan komma som en obehaglig överraskning.
              </p>

              <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-4">
                <div>
                  <h4 className="font-bold mb-2 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    Lagfart
                  </h4>
                  <p className="text-sm">
                    Fungerar som ägandebevis för huset. Kostar <strong>1,5%</strong> av 
                    priset eller taxeringsvärdet (det högsta beloppet räknas). 
                    Expeditionsavgift: <strong>825 kr</strong>.
                  </p>
                </div>

                <div>
                  <h4 className="font-bold mb-2 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    Pantbrev
                  </h4>
                  <p className="text-sm">
                    Värdebevis för att din bostad pantsatts hos banken. Kostar <strong>2%</strong> av 
                    beloppet (stämpelskatt) plus <strong>375 kr</strong> per brev. 
                    Pantbrev är knutna till fastigheten och tas över av nya ägaren vid försäljning.
                  </p>
                  <Button variant="link" className="px-0 h-auto mt-2" onClick={() => navigate(`/${currentLanguage}/guider/pantbrev`)}>
                    Läs mer om pantbrev
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>

              <h3 className="text-2xl font-bold mt-8 mb-4">Räntan på ditt bostadslån</h3>
              
              <p>
                Den bolåneränta du erbjuds avgörs av flera faktorer:
              </p>

              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Dina personliga ekonomiska förutsättningar</li>
                <li>Din belåningsgrad (hur stor del av bostadens värde du lånar)</li>
                <li>Bostaden du söker lån för</li>
                <li>Vald bindningstid för räntan</li>
              </ul>

              <p>
                Du kan välja mellan <strong>bunden eller rörlig ränta</strong> för ditt bolån:
              </p>

              <div className="grid md:grid-cols-2 gap-6 my-6">
                <Card className="border-2">
                  <CardHeader>
                    <CardTitle>Bunden ränta</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">
                      Fast ränta under hela bindningstiden (1-10 år). Ger trygghet och förutsägbarhet, 
                      men kan vara högre än rörlig ränta.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2">
                  <CardHeader>
                    <CardTitle>Rörlig ränta</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">
                      Räntan är bunden på tre månader i taget och följer marknadens utveckling. 
                      Kan ge lägre ränta men också större osäkerhet.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Alert className="border-primary/50 bg-primary/5">
                <Info className="h-5 w-5" />
                <AlertDescription>
                  <strong>Tips:</strong> Du kan dela upp ditt bolån och ha både bunden och rörlig 
                  ränta på olika delar för att balansera risk och trygghet.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </section>

        {/* Räntekollen Section */}
        <section className="py-8 md:py-12 bg-gradient-to-br from-primary via-primary to-primary-dark">
          <div className="container max-w-3xl">
            <Suspense fallback={<CalculatorSkeleton variant="detailed" />}>
              <InterestRateChecker type="mortgage" initialAmount={3000000} />
            </Suspense>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-8 md:py-12 bg-muted/30">
          <div className="container max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              Vanliga frågor om bolån
            </h2>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-left">
                  Hur fungerar ett bolån?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  När du tar ett bolån lånar du pengar av en bank eller långivare för att köpa en bostad. 
                  Bostaden fungerar som säkerhet för lånet, vilket betyder att banken kan ta över bostaden 
                  om lånet inte betalas. Du betalar sedan tillbaka lånet månadsvis med ränta och{" "}
                  <Link 
                    to={`/${currentLanguage}/guider/amortera-eller-spara`}
                    className="text-primary hover:underline font-medium"
                  >
                    amortering
                  </Link>.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger className="text-left">
                  Hur mycket kan jag låna till en bostad?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  Hur mycket du kan låna beror på din inkomst, skuldsättning och bostadens värde. 
                  Vanligtvis får du låna upp till 85 % av bostadens marknadsvärde, medan minst 15 % 
                  måste vara{" "}
                  <Link 
                    to={`/${currentLanguage}/guider/kontantinsats-och-bolanetak`}
                    className="text-primary hover:underline font-medium"
                  >
                    kontantinsats
                  </Link>. Läs mer om{" "}
                  <Link 
                    to={`/${currentLanguage}/guider/belåningsgrad`}
                    className="text-primary hover:underline font-medium"
                  >
                    belåningsgrad
                  </Link> för att förstå hur mycket du kan låna.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger className="text-left">
                  Vad innebär amortering?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  <p className="mb-4">
                    Amortering betyder att du betalar av på själva skulden varje månad. Hur mycket du 
                    måste amortera beror på hur stort ditt lån är i förhållande till bostadens värde och 
                    din inkomst:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mb-4">
                    <li>Lån över 70 % av bostadens värde: minst 2 % per år</li>
                    <li>Lån mellan 50–70 %: minst 1 % per år</li>
                    <li>Under 50 %: ingen krav på amortering</li>
                  </ul>
                  <p>
                    Läs mer i vår guide om{" "}
                    <Link 
                      to={`/${currentLanguage}/guider/amortera-eller-spara`}
                      className="text-primary hover:underline font-medium"
                    >
                      amortering
                    </Link>.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4">
                <AccordionTrigger className="text-left">
                  Vad påverkar bolåneräntan?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  <p className="mb-4">
                    Räntan påverkas av flera faktorer, bland annat:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      <Link 
                        to={`/${currentLanguage}/guider/reporanta`}
                        className="text-primary hover:underline font-medium"
                      >
                        Riksbankens styrränta
                      </Link>
                    </li>
                    <li>Bankens riskbedömning och kostnader</li>
                    <li>Hur stor del av bostaden du lånar till</li>
                    <li>
                      Val av{" "}
                      <Link 
                        to={`/${currentLanguage}/guider/ranta-rorlig-eller-bunden`}
                        className="text-primary hover:underline font-medium"
                      >
                        rörlig eller bunden ränta
                      </Link>
                    </li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5">
                <AccordionTrigger className="text-left">
                  Vad är ett lånelöfte och när får jag det?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  Ett{" "}
                  <Link 
                    to={`/${currentLanguage}/guider/lanelöfte`}
                    className="text-primary hover:underline font-medium"
                  >
                    lånelöfte
                  </Link>{" "}
                  är en preliminär bekräftelse på hur mycket en bank är villig att 
                  låna ut baserat på din ekonomiska situation. Det hjälper dig att förstå din 
                  budget och är ofta ett krav när du köper bostad.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6">
                <AccordionTrigger className="text-left">
                  Vad är belåningsgrad och hur påverkar den mitt lån?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  <Link 
                    to={`/${currentLanguage}/guider/belåningsgrad`}
                    className="text-primary hover:underline font-medium"
                  >
                    Belåningsgraden
                  </Link>{" "}
                  är andelen av bostadens värde som du lånar. Ju högre belåningsgrad, 
                  desto högre risk för långivaren och därför kan räntan bli högre.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7">
                <AccordionTrigger className="text-left">
                  Hur kan jag jämföra olika bolån och hitta den bästa räntan?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  Det är viktigt att jämföra räntor, villkor och avgifter mellan olika långivare 
                  för att hitta det bästa erbjudandet. Faktorer som{" "}
                  <Link 
                    to={`/${currentLanguage}/guider/ranta-rorlig-eller-bunden`}
                    className="text-primary hover:underline font-medium"
                  >
                    rörlig eller bunden ränta
                  </Link>, 
                  uppläggningsavgifter och amorteringsvillkor spelar också in.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        <ReviewedBy variant="default" />

        {/* CTA Section */}
        <section className="py-8 md:py-12 bg-gradient-to-br from-primary/10 via-primary/5 to-background">
          <div className="container max-w-4xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Redo att jämföra bolån?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Använd vår kalkylator för att se din månadskostnad och börja jämföra 
              erbjudanden från olika banker.
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" onClick={scrollToRatesTable}>
                Jämför bolån nu
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              
              <Button variant="outline" size="lg" onClick={() => navigate(`/${currentLanguage}/guider/belåningsgrad`)}>
                Läs om belåningsgrad
              </Button>
              
              <Button variant="outline" size="lg" onClick={() => navigate(`/${currentLanguage}/guider/lanelöfte`)}>
                Läs om lånelöfte
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>

      <StickyCTA show={showStickyCTA}>
        <div className="container mx-auto max-w-5xl px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">Ordna Bolån</p>
              <p className="truncate text-xs text-muted-foreground">Jämför flera banker — personlig rådgivning</p>
            </div>
            <Button asChild size="lg" className="whitespace-nowrap bg-primary text-white hover:bg-primary/90">
              <a
                href="https://go.adt256.com/t/t?a=1137286168&as=2024680921&t=2&tk=1"
                target="_blank"
                rel="sponsored nofollow noopener noreferrer"
              >
                Till Ordna Bolån
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </StickyCTA>
    </div>;
};
export default MortgagePage;
